import difference from 'lodash/difference';
import isNil from 'lodash/isNil';

import { danger } from 'redux-store/reducers/common-reducer';

import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import { isDateRangeEmpty } from '../utils/general';

import {
  saveStorySnippets,
  searchStoriesForDeliverable
} from '../services/story-snippets';

applyTranslations(require('./select-story-snippets-actions.i18n.json'));

import { State } from 'types';
import {
  ChangeDateRangeAction,
  ChangeStorySnippetPreviewAction,
  ChangeViewModeAction,
  DateRange,
  FlushStateAction,
  OnSelectCallback,
  SetCampaignStartDateAction,
  SetDeliverableIdAction,
  SetInitialSelectedStorySnippetsAction,
  StorySnippet,
  ToggleStorySnippetAction,
  UpdateOnSelectCallbackAction,
  UpdateSaveOnSelectAction,
  UpdateSearchByAction,
  UpdateStorySnippetsAction,
  ViewMode
} from '../types';

export const changeDateRangeAction = (dates: DateRange): ChangeDateRangeAction => ({
  type: 'change-date-range',
  payload: { dates }
});

export const updateSearchByAction = (keywords: Array<string>): UpdateSearchByAction => ({
  type: 'update-search-by',
  payload: { keywords }
});

export const updateStorySnippetsAction = (
  snippets: Array<StorySnippet>
): UpdateStorySnippetsAction => ({
  type: 'update-story-snippets',
  payload: { snippets }
});

export const toggleStorySnippetAction = (snippetId: string): ToggleStorySnippetAction => ({
  type: 'toggle-story-snippet',
  payload: { snippetId }
});

export const changeStorySnippetPreviewAction = (
  snippetId: string
): ChangeStorySnippetPreviewAction => ({
  type: 'change-story-snippet-preview',
  payload: { snippetId }
});

const updateOnSelectCallbackAction = (
  cb: OnSelectCallback
): UpdateOnSelectCallbackAction => ({
  type: 'update-on-select-callback',
  payload: { cb }
});

const updateSaveOnSelectAction = (saveChangesOnClose: boolean): UpdateSaveOnSelectAction => ({
  type: 'update-save-on-select',
  payload: { saveChangesOnClose }
});

const setDeliverableIdAction = (deliverableId: number): SetDeliverableIdAction => ({
  type: 'set-deliverable-id',
  payload: { deliverableId }
});

export const flushStateAction = (): FlushStateAction => ({
  type: 'flush-state'
});
const setInitialSelectedStorySnippetsAction = (
  selectedStorySnippets: Array<string>
): SetInitialSelectedStorySnippetsAction => ({
  type: 'set-initial-selected-story-snippets',
  payload: { selectedStorySnippets }
});

export const changeViewModeAction = (viewMode: ViewMode): ChangeViewModeAction => ({
  type: 'change-view-mode',
  payload: { viewMode }
});

export const setCampaignStartDateAction = (
  campaignStartDate: null | string
): SetCampaignStartDateAction => ({
  type: 'set-campaign-start-date',
  payload: { campaignStartDate }
});

export const updateStorySnippetsList = () => (
  async (dispatch, getState: () => State) => {
    try {
      const { dates, searchBy, deliverableId } = getState().selectStorySnippets;

      if (deliverableId === null) {
        throw new Error('Request story snippets / deliverableId is null');
      }

      const snippets = await searchStoriesForDeliverable(deliverableId, dates, searchBy);

      dispatch(updateStorySnippetsAction(snippets));
    } catch (error) {
      logError(error);
      danger(__t('select_story_snippets_actions.error_loading'));
    }
  }
);

export const init = (
  deliverableId: number,
  campaignStartDate: string,
  onSelectCallback: OnSelectCallback,
  selectedStorySnippets?: Array<string>,
  saveChangesOnClose?: boolean
) => async dispatch => {
  try {
    dispatch(updateOnSelectCallbackAction(onSelectCallback));
    dispatch(setDeliverableIdAction(deliverableId));
    dispatch(setCampaignStartDateAction(campaignStartDate));

    if (!isNil(saveChangesOnClose)) {
      dispatch(updateSaveOnSelectAction(saveChangesOnClose));
    }

    if (!isNil(selectedStorySnippets)) {
      dispatch(setInitialSelectedStorySnippetsAction(selectedStorySnippets));
    }

    const snippets = await searchStoriesForDeliverable(deliverableId);

    dispatch(updateStorySnippetsAction(snippets));
  } catch (error) {
    logError(error);
    danger(__t('select_story_snippets_actions.error_loading'));
  }
};

export const onSaveSnippets = (
  deliverableId: number
) => async (dispatch, getState: () => State) => {
  try {
    let { selectStorySnippets } = getState();

    if (isDateRangeEmpty(selectStorySnippets.dates) || selectStorySnippets.searchBy.length === 0) {
      const snippets = await searchStoriesForDeliverable(deliverableId);

      dispatch(updateStorySnippetsAction(snippets));
    }

    selectStorySnippets = getState().selectStorySnippets;
    const selectedSnippets = (selectStorySnippets.storySnippets ?? [])
      .filter(s => selectStorySnippets.selectedStorySnippets.includes(s.code));
    const persistSnippets = async () => {
      const originalSelections = (selectStorySnippets.storySnippets ?? [])
        .filter(s => s.selected)
        .map(s => s.code);

      const removed = difference(originalSelections, selectStorySnippets.selectedStorySnippets);
      const added = difference(selectStorySnippets.selectedStorySnippets, originalSelections);

      if (added.length > 0 || removed.length > 0) {
        await saveStorySnippets(deliverableId, added, removed);
      }
    };

    if (!selectStorySnippets.saveChangesOnClose) {
      if (selectStorySnippets.onSelectCallback) {
        selectStorySnippets.onSelectCallback(selectedSnippets);
      }

      return;
    }

    await persistSnippets();

    if (selectStorySnippets.onSelectCallback) {
      selectStorySnippets.onSelectCallback(selectedSnippets);
    }
  } catch (error) {
    logError(error);
    danger(__t('select_story_snippets_actions.error_loading'));
  }
};
